import React from 'react';
/**
 * @type {{(effect: React.EffectCallback, deps?: React.DependencyList): void,
 * (effect: React.EffectCallback, deps?: React.DependencyList): void}}
 */

var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;
/**
 * @type {boolean}
 */

var isBrowser = typeof window !== 'undefined';
/**
 * @param element
 * @param useWindow
 * @returns {{width: (number), height: (number)}}
 */

function getInnerDimensions(_ref) {
  var element = _ref.element,
      useWindow = _ref.useWindow;
  if (!isBrowser) return {
    width: 0,
    height: 0
  };
  var target = element ? element.current : document.body;
  var position = target.getBoundingClientRect();
  var width = useWindow ? window.innerWidth : position.innerWidth;
  var height = useWindow ? window.innerHeight : position.innerHeight;
  return {
    width: width,
    height: height
  };
}
/**
 * Returns the current and previous inner dimensions of the specified element, defaults to window
 * @param {function} effect Callback
 * @param {array} deps Dependencies that trigger a callback
 * @param {ref} element Get scroll position for certain element
 * @param {boolean} useWindow
 * @param {number} wait In Milliseconds to wait between calls
 */


function useInnerDimensions(effect, deps) {
  var element = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var useWindow = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  var wait = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 100;
  var position = React.useRef(getInnerDimensions({
    useWindow: useWindow
  }));
  var throttleTimeout = null;

  var callBack = function callBack() {
    var currDimensions = getInnerDimensions({
      element: element,
      useWindow: useWindow
    });
    effect({
      prevDimensions: position.current,
      currDimensions: currDimensions
    });
    position.current = currDimensions;
    throttleTimeout = null;
  };
  /** This is used to return something in the first render, even if the user did not resize * */


  React.useEffect(function () {
    callBack();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);
  useIsomorphicLayoutEffect(function () {
    if (!isBrowser) {
      return;
    }

    var handleResize = function handleResize() {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('resize', handleResize);
    /* eslint-disable consistent-return */

    return function () {
      window.removeEventListener('resize', handleResize);
      throttleTimeout && clearTimeout(throttleTimeout);
    };
  }, deps);
}

useInnerDimensions.defaultProps = {
  deps: [],
  element: false,
  useWindow: false,
  wait: null
};
export default useInnerDimensions;